import { IconDeviceMobile } from "@tabler/icons-react";
import { buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { config } from "@/data/config";
import {
	getMobileAppDistribution,
	hasMobileAppStoreLinks,
	type MobileAppDistribution,
} from "@/lib/mobile-app";
import { cn } from "@/lib/utils";

const storeLinkClass =
	"inline-flex items-center justify-center rounded-full border border-border/80 bg-card px-5 py-2.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted/60";

export function MobileAppStoreLinks({
	distribution,
	className,
}: {
	distribution: MobileAppDistribution;
	className?: string;
}) {
	const { iosAppStoreUrl, googlePlayUrl } = distribution;
	if (!iosAppStoreUrl && !googlePlayUrl) {
		return null;
	}

	return (
		<div
			className={cn(
				"flex flex-wrap items-center justify-center gap-3",
				className,
			)}
		>
			{iosAppStoreUrl ? (
				<a
					href={iosAppStoreUrl}
					className={storeLinkClass}
					target="_blank"
					rel="noopener noreferrer"
				>
					App Store
				</a>
			) : null}
			{googlePlayUrl ? (
				<a
					href={googlePlayUrl}
					className={storeLinkClass}
					target="_blank"
					rel="noopener noreferrer"
				>
					Google Play
				</a>
			) : null}
		</div>
	);
}

/** Inline store buttons for marketing pages and download landing. */
export function MobileAppCta({ className }: { className?: string }) {
	const distribution = getMobileAppDistribution();
	if (!hasMobileAppStoreLinks(distribution)) {
		return null;
	}

	return (
		<div
			className={cn(
				"flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center",
				className,
			)}
		>
			<p className="text-sm font-medium text-muted-foreground">
				Get the {config.siteName} app
			</p>
			<MobileAppStoreLinks distribution={distribution} />
		</div>
	);
}

/** Settings / account surfaces for logged-in users. */
export function MobileAppDistributionCard() {
	const distribution = getMobileAppDistribution();
	if (!hasMobileAppStoreLinks(distribution)) {
		return null;
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2 text-lg">
					<IconDeviceMobile className="size-5" aria-hidden />
					Mobile app
				</CardTitle>
				<CardDescription>
					Use {config.siteName} on iOS or Android. Sign in with the same account
					as the web app.
				</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<MobileAppStoreLinks
					distribution={distribution}
					className="justify-start"
				/>
				<a
					href="/download"
					className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
				>
					Download details
				</a>
			</CardContent>
		</Card>
	);
}
